/**
 * Read data usage out of an OCS `subscriberUsageOverPeriod` response.
 *
 * WHY THIS IS SHARED
 * ------------------
 * Three surfaces read this response and all three read it differently:
 *
 *   apps/mcp-server   guarded on Array.isArray(usage.data)  -> never true
 *   apps/mcp-stdio    same guard, same result
 *   apps/api          reads `data.totalBytes`               -> field does not exist
 *
 * The live response, captured from the fleet, is:
 *
 *   {
 *     total:  { cost, parentResellerCost, resellerCost, subscriberCost },
 *     usages: [ { subscriberId, subsPeriodUsages: [
 *                 { day, total: { quantityPerType: { "33": <bytes> } } } ] } ]
 *   }
 *
 * There is a `total`, but it carries COST fields — no byte count. So
 * `data.totalBytes ?? 0` yields 0 for every subscriber, and a ranking built on
 * it ranks everything equal at zero.
 *
 * The data quantity type is the string "33". Not "DATA" — a readable-looking
 * key silently matches nothing, which is how a first draft of the mcp-server
 * test asserted the wrong thing.
 */

/** OCS quantity type code for data. Voice and SMS use other codes. */
export const OCS_DATA_QUANTITY_TYPE = "33";

export interface DailyUsage {
  date: string;
  bytes: number;
}

function readBytes(entry: Record<string, unknown>): number {
  const total = entry.total;
  if (total !== null && typeof total === "object") {
    const perType = (total as Record<string, unknown>).quantityPerType;
    if (perType !== null && typeof perType === "object") {
      const v = (perType as Record<string, unknown>)[OCS_DATA_QUANTITY_TYPE];
      if (v !== undefined) return Number(v) || 0;
    }
  }
  // Legacy/flat shapes, kept so recorded fixtures and older OCS keep working.
  return Number(entry.dataBytes ?? entry.dataVolume ?? entry.totalData ?? 0) || 0;
}

/**
 * Daily rows from a usage response, in the order OCS returned them.
 *
 * Accepts the nested envelope and the legacy flat array. Returns [] for
 * anything else, so an absent response and a zero-usage subscriber look the
 * same to callers — which is correct, since both mean "nothing to report".
 */
export function extractDailyUsage(data: unknown): DailyUsage[] {
  const out: DailyUsage[] = [];
  if (data === null || typeof data !== "object") return out;

  if (Array.isArray(data)) {
    for (const entry of data) {
      if (entry === null || typeof entry !== "object") continue;
      const e = entry as Record<string, unknown>;
      out.push({ date: String(e.date ?? e.day ?? "?"), bytes: readBytes(e) });
    }
    return out;
  }

  const usages = (data as Record<string, unknown>).usages;
  if (!Array.isArray(usages)) return out;
  for (const usage of usages) {
    if (usage === null || typeof usage !== "object") continue;
    const periods = (usage as Record<string, unknown>).subsPeriodUsages;
    if (!Array.isArray(periods)) continue;
    for (const period of periods) {
      if (period === null || typeof period !== "object") continue;
      const p = period as Record<string, unknown>;
      out.push({ date: String(p.day ?? p.date ?? "?"), bytes: readBytes(p) });
    }
  }
  return out;
}

/** Total data bytes over the window. This is what `totalBytes` was meant to be. */
export function totalUsageBytes(data: unknown): number {
  return extractDailyUsage(data).reduce((sum, r) => sum + r.bytes, 0);
}

/** Mean daily bytes, or 0 when there are no rows. */
export function averageDailyBytes(data: unknown): number {
  const rows = extractDailyUsage(data);
  return rows.length === 0 ? 0 : rows.reduce((s, r) => s + r.bytes, 0) / rows.length;
}

/**
 * Total cost over the window, read from the `total` object that DOES exist.
 *
 * Separate from bytes on purpose: conflating the two is what produced a
 * `totalBytes` read against a cost-only object in the first place.
 */
export function totalUsageCost(data: unknown): number {
  if (data === null || typeof data !== "object") return 0;
  const total = (data as Record<string, unknown>).total;
  if (total === null || typeof total !== "object") return 0;
  return Number((total as Record<string, unknown>).cost ?? 0) || 0;
}
